import React, { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoSearchSharp } from 'react-icons/io5';
import { useSearchParams, useNavigate } from 'react-router';
import api from '../../utils/api';
import { IoFilterSharp } from 'react-icons/io5';
import { useRef } from 'react';

function LeaveBalanceReports() {
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestsPerPage] = useState(8);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [managers, setManagers] = useState([]);
const [selectedManager, setSelectedManager] = useState('');
const [selectedLocation, setSelectedLocation] = useState('');

const [draftManager, setDraftManager] = useState("");
const [draftLocation, setDraftLocation] = useState("");

  const [selectedRows, setSelectedRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const [filterOpen, setFilterOpen] = useState(false);
const activeFilterCount = [
  selectedManager,
  selectedLocation
].filter(Boolean).length;

// Click outside to close
const filterRef = useRef(null);
useEffect(() => {
  const handleClickOutside = (e) => {
    if (filterRef.current && !filterRef.current.contains(e.target)) {
      setFilterOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

  const handleSelectRow = (id) => {
  setSelectedRows(prev =>
    prev.includes(id)
      ? prev.filter(r => r !== id)
      : [...prev, id]
  );
};

const handleSelectAll = () => {
  const allIds = filteredBalances.map(r => r.id);
  const allSelected = allIds.every(id => selectedRows.includes(id));

  setSelectedRows(allSelected ? [] : allIds);
};

const handleExport = async () => {
  if (selectedRows.length === 0) return;

  setExportLoading(true);
  try {
    const response = await api.get(`/api/leave-balance/export`, {
      params: { ids: selectedRows.join(',') },
      responseType: "blob",   // ← required for file download
    });

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "leave_balance_report.xlsx";
    link.click();
    URL.revokeObjectURL(link.href);

  } catch (error) {
    console.error("Export failed", error);
  } finally {
    setExportLoading(false);
  }
};

const applySearch = () => {
  navigate(`?search=${searchInput}`);
  setCurrentPage(1);
};

  const fetchLeaveBalances = async (page = 1, size = requestsPerPage) => {
    setApiLoading(true);
    try {
      const response = await api.get(`/api/leave-balance/all`, {
        params: {
          page: page - 1,
          size,
          sortBy: 'user.id',
          direction: 'asc',
          managerId: selectedManager || null,
          location: selectedLocation || null,
        },
      });

      const data = response.data;

      console.log('Fetched leave balances:', data);

      const mappedData = data.content.map((item) => ({
        id: item.id,
        userId: item.employeeId,
        name: item.userName,
        totalEarnedLeaves: item.totalEarnedLeaves ?? 0,
        remainingEarnedLeaves: item.earnedLeaves ?? 0,
        usedEarnedLeaves: parseFloat(((item.totalEarnedLeaves ?? 0) - (item.earnedLeaves ?? 0)).toFixed(1)),
        totalSickLeaves: item.totalSickLeaves ?? 0,
        remainingSickLeaves: item.sickLeaves ?? 0,
        usedSickLeaves: parseFloat(((item.totalSickLeaves ?? 0) - (item.sickLeaves ?? 0)).toFixed(1)),
        compOffs: item.compOffs ?? 0,
      }));

      setLeaveBalances(mappedData);
      setTotalPages(data.totalPages);
      setCurrentPage(data.number + 1);
    } catch (error) {
      console.error('Failed to fetch leave balances:', error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
  fetchManagers();
}, []);

  useEffect(() => {
    fetchLeaveBalances(currentPage);
  }, [currentPage, selectedManager, selectedLocation]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeaveBalances(1);
  }, [searchParams]);

  const filteredBalances = leaveBalances.filter((b) => {
    const search = (searchParams.get('search') || '').toLowerCase();
    if (!search) return true;
    return (
      b.name?.toLowerCase().includes(search) ||
      b.userId?.toString().toLowerCase().includes(search)
    );
  });


const fetchManagers = async () => {
  try {
    const response = await api.get('/auth/managers');
    setManagers(response.data || []);
  } catch (error) {
    console.error('Failed to fetch managers', error);
  }
};

  const StatPill = ({ value, color }) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-700',
      green: 'bg-green-50 text-green-700',
      red: 'bg-red-50 text-red-700',
      amber: 'bg-amber-50 text-amber-700',
      purple: 'bg-purple-50 text-purple-700',
    };
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors[color] || 'bg-slate-100 text-slate-600'}`}>
        {value}
      </span>
    );
  };

  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-56 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-[400px] flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      {/* Header Bar */}
<div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-1 mb-4 bg-white rounded-2xl p-4">
    <div>
    <h2 className="text-xl font-semibold text-slate-800">Leave Balance Reports</h2>
    <p className="text-sm text-slate-400">{leaveBalances.length} records shown</p>
  </div>
  

{/* Replace the existing filter+search div with this */}
<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative">

    {/* Search */}
<div className="relative">
  <input
    type="text"
    placeholder="Search employee..."
    value={searchInput}
    onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
    onKeyDown={(e) => {
      if (e.key === 'Enter') applySearch();             // apply on Enter
    }}
    className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
  />
  <IoSearchSharp
    onClick={applySearch}                               // apply on icon click
    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
  />
</div>

  {/* Export Button */}
  <button
    onClick={handleExport}
    disabled={selectedRows.length === 0 || exportLoading}
    className={`px-5 py-2.5 rounded-full text-xs font-medium transition ${
      selectedRows.length === 0
        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
        : "bg-green-600 text-white hover:bg-green-700"
    }`}
  >
    {exportLoading ? "Exporting..." : `Export (${selectedRows.length})`}
  </button>

  {/* Filter Icon Button */}
  <div className="relative" ref={filterRef}>
    <button
      onClick={() => {
    if (!filterOpen) {
      // Sync draft to applied when opening
      
      setDraftManager(selectedManager);
      setDraftLocation(selectedLocation);
    }
    setFilterOpen(prev => !prev);
  }}
      className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-medium transition
        ${activeFilterCount > 0
          ? 'border-slate-700 bg-slate-700 text-white'
          : 'bg-white text-slate-600 hover:bg-slate-100'
        }`}
    >
      <IoFilterSharp className="text-base" />
      <span>Filter</span>
      {activeFilterCount > 0 && (
        <span className="ml-1 bg-white text-slate-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
          {activeFilterCount}
        </span>
      )}
    </button>

    {/* Filter Dropdown Panel */}
    {filterOpen && (
      <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-4 space-y-4">
        
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-slate-700">Filters</span>
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                    setDraftManager("");
    setDraftLocation("");
                setSelectedManager('');
                setSelectedLocation('');
                setCurrentPage(1);
              }}
              className="text-xs text-red-500 hover:text-red-600 font-medium"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Manager Filter */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Manager</label>
          <select
            value={draftManager}
            onChange={(e) => {
              setDraftManager(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <option value="">All Managers</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
            ))}
          </select>
        </div>


{/* Location Filter */}
<div>
  <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
  <div className="flex gap-2">
    {['Bengaluru', 'Mangaluru'].map((loc) => (
      <button
        key={loc}
        onClick={() => {
          setDraftLocation(prev => prev === loc ? '' : loc);
        }}
        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition
          ${draftLocation === loc
            ? 'bg-slate-700 text-white border-slate-700'
            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
          }`}
      >
        {loc}
      </button>
    ))}
  </div>
</div>



<button
  onClick={() => {
    setSelectedManager(draftManager);
    setSelectedLocation(draftLocation);
    setCurrentPage(1);
    setFilterOpen(false);
  }}
  className="w-full bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-600 transition"
>
  Apply Filters
</button>

      </div>
    )}
  </div>


</div>
</div>

      {/* Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
<thead className="border-b border-slate-100 bg-slate-50">
  {/* Row 1: Group labels */}
  <tr>
      <th className="px-3 text-left" rowSpan={2}>
    <input
      type="checkbox"
      checked={filteredBalances.length > 0 && filteredBalances.every(r => selectedRows.includes(r.id))}
      onChange={handleSelectAll}
      className="w-4 h-4 "
    />
  </th>

    <th rowSpan={2} className="px-3 py-4 text-xs font-semibold text-slate-500 text-left tracking-wide">Sl. No</th>
    <th rowSpan={2} className="px-3 py-4 text-xs font-semibold text-slate-500 text-left tracking-wide">Employee ID</th>
    <th rowSpan={2} className="px-3 py-4 text-xs font-semibold text-slate-500 text-left tracking-wide">Employee Name</th>
    <th colSpan={3} className="px-3 pt-3 pb-1 text-xs font-semibold text-blue-600 text-center tracking-wide">
      Earned Leave
    </th>
    <th colSpan={3} className="px-3 pt-3 pb-1 text-xs font-semibold text-green-600 text-center tracking-wide">
      Sick Leave
    </th>
    <th rowSpan={2} className="px-3 py-4 text-xs font-semibold text-purple-600 text-center tracking-wide">Comp Off</th>
  </tr>

  {/* Row 2: Sub-column labels */}
  <tr>
   
    {/* Earned Leave sub-cols */}
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Total</th>
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Used</th>
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Remaining</th>
    {/* Sick Leave sub-cols */}
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Total</th>
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Used</th>
    <th className="px-3 pb-3 text-[10px] font-medium text-slate-400 text-center">Remaining</th>
  </tr>
</thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : filteredBalances.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-slate-500">
                    No leave balance records found.
                  </td>
                </tr>
              ) : (
                filteredBalances.map((emp, index) => (
                  <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors duration-150">

                      {/* Checkbox column */}
  <td className="px-3 py-3 align-middle">
    <input
      type="checkbox"
      checked={selectedRows.includes(emp.id)}
      onChange={() => handleSelectRow(emp.id)}
      className="w-4 h-4"
    />
  </td>


                    <td className="px-3 py-3 text-sm text-slate-500">
                      {(currentPage - 1) * requestsPerPage + index + 1}
                    </td>
                    
                    <td className="px-3 py-3 text-sm text-slate-500">{emp.userId}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-800">{emp.name}</td>
                    {/* Earned Leave */}
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.totalEarnedLeaves} color="blue" />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.usedEarnedLeaves} color="red" />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.remainingEarnedLeaves} color="blue" />
                    </td>

                    {/* Sick Leave */}
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.totalSickLeaves} color="green" />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.usedSickLeaves} color="red" />
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.remainingSickLeaves} color="green" />
                    </td>

                    {/* Comp Off */}
                    <td className="px-3 py-3 text-center">
                      <StatPill value={emp.compOffs} color="purple" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredBalances.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': { color: '#475569', borderColor: '#e2e8f0' },
                    '& .Mui-selected': { backgroundColor: '#475569 !important', color: '#fff' },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {apiLoading ? (
          <div className="flex justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        ) : filteredBalances.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No records found.</p>
        ) : (
          filteredBalances.map((emp, index) => (
            <motion.div
              key={emp.id}
              className="bg-white rounded-2xl shadow p-4 space-y-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{emp.name}</p>
                  <p className="text-xs text-slate-400">{emp.userId}</p>
                </div>
                <span className="text-xs text-slate-400">#{(currentPage - 1) * requestsPerPage + index + 1}</span>
              </div>

              {/* EL Section */}
              <div>
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1.5">Earned Leave</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Total</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">{emp.totalEarnedLeaves}</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Used</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">{emp.usedEarnedLeaves}</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Remaining</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">{emp.remainingEarnedLeaves}</span>
                  </div>
                </div>
              </div>

              {/* SL Section */}
              <div>
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1.5">Sick Leave</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Total</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">{emp.totalSickLeaves}</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Used</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">{emp.usedSickLeaves}</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 mb-1">Remaining</p>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">{emp.remainingSickLeaves}</span>
                  </div>
                </div>
              </div>

              {/* Comp Off */}
              <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Comp Off</p>
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">{emp.compOffs}</span>
              </div>
            </motion.div>
          ))
        )}

        {/* Mobile Pagination */}
        {filteredBalances.length > 0 && (
          <div className="flex justify-center pt-2 pb-4">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="full"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': { color: '#475569', borderColor: '#e2e8f0' },
                  '& .Mui-selected': { backgroundColor: '#475569 !important', color: '#fff' },
                }}
              />
            </Stack>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeaveBalanceReports;